import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FlaskConical,
  ArrowLeft,
  Download,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Eye,
  FileText,
  User,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getPatientResults,
  getResultById,
  getResultPDF,
  LabResult,
  ResultObservation,
  getInterpretationLabel,
  getInterpretationColor,
} from '@/services/labService';

// Interpretation Icon Component
const InterpretationIcon: React.FC<{ interpretation: string }> = ({ interpretation }) => {
  switch (interpretation) {
    case 'H':
      return <TrendingUp className="h-4 w-4 text-red-500" />;
    case 'L':
      return <TrendingDown className="h-4 w-4 text-blue-500" />;
    case 'C':
      return <AlertTriangle className="h-4 w-4 text-red-700" />;
    default:
      return <Minus className="h-4 w-4 text-emerald-500" />;
  }
};

// Result Observation Row Component
const ObservationRow: React.FC<{ observation: ResultObservation }> = ({ observation }) => {
  const interpretationColor = getInterpretationColor(observation.interpretation);

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50/50">
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900">{observation.markerName || 'Unknown Marker'}</div>
        {observation.loincCode && (
          <div className="text-xs text-gray-400">LOINC: {observation.loincCode}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`font-semibold ${interpretationColor}`}>
            {observation.value}
          </span>
          <span className="text-gray-500 text-sm">{observation.unit}</span>
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-gray-600 text-sm">{observation.referenceRange || '-'}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <InterpretationIcon interpretation={observation.interpretation} />
          <span className={interpretationColor}>
            {(observation.interpretation === 'N' || observation.interpretation === 'normal') ? 'Normal' : 'Abnormal'}
          </span>
        </div>
      </td>
    </tr>
  );
};

// Result Card Component
const ResultCard: React.FC<{
  result: LabResult;
  onViewDetails: (result: LabResult) => void;
  onDownloadPDF: (resultId: string) => void;
}> = ({ result, onViewDetails, onDownloadPDF }) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Count observations by interpretation
  const observations = result.observations || [];
  const stats = observations.reduce(
    (acc, obs) => {
      if (obs.interpretation === 'N') acc.normal++;
      else if (obs.interpretation === 'H') acc.high++;
      else if (obs.interpretation === 'L') acc.low++;
      else if (obs.interpretation === 'C') acc.critical++;
      return acc;
    },
    { normal: 0, high: 0, low: 0, critical: 0 }
  );

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <FlaskConical className="h-5 w-5 text-primary" />
              {result.order?.panel?.name || 'Lab Results'}
            </CardTitle>
            <CardDescription className="flex items-center gap-4 mt-1">
              <span className="flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(result.received_at)}
              </span>
              <Badge
                className={
                  result.status === 'final'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }
              >
                {result.status}
              </Badge>
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onViewDetails(result)}>
              <Eye className="h-4 w-4 mr-1" />
              View
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDownloadPDF(result.id)}>
              <Download className="h-4 w-4 mr-1" />
              PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats Summary */}
        <div className="flex gap-4 mb-4">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-sm text-gray-600">{stats.normal} Normal</span>
          </div>
          {stats.high > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <span className="text-sm text-red-600">{stats.high} High</span>
            </div>
          )}
          {stats.low > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-sm text-blue-600">{stats.low} Low</span>
            </div>
          )}
          {stats.critical > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-700" />
              <span className="text-sm text-red-700 font-medium">{stats.critical} Critical</span>
            </div>
          )}
        </div>

        {/* Critical/Abnormal Values Preview */}
        {result.has_critical_values && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium text-sm">Critical values detected - review required</span>
            </div>
          </div>
        )}

        {/* Preview of abnormal results */}
        {(stats.high > 0 || stats.low > 0) && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Abnormal results: </span>
            {observations
              .filter(o => o.interpretation !== 'N')
              .slice(0, 3)
              .map(o => o.markerName || 'Unknown')
              .join(', ')}
            {observations.filter(o => o.interpretation !== 'N').length > 3 && '...'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Result Detail View Component
const ResultDetailView: React.FC<{
  result: LabResult;
  onBack: () => void;
  onDownloadPDF: (resultId: string) => void;
}> = ({ result, onBack, onDownloadPDF }) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Group observations by category (based on marker name patterns)
  const observations = result.observations || [];
  const groupedObservations = observations.reduce((acc, obs) => {
    // Simple grouping - could be enhanced with actual category data
    const category = 'All Results';
    if (!acc[category]) acc[category] = [];
    acc[category].push(obs);
    return acc;
  }, {} as Record<string, ResultObservation[]>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Results
          </Button>
        </div>
        <Button onClick={() => onDownloadPDF(result.id)} className="gap-2">
          <Download className="h-4 w-4" />
          Download PDF
        </Button>
      </div>

      {/* Result Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <FlaskConical className="h-6 w-6 text-primary" />
                {result.order?.panel?.name || 'Lab Results'}
              </CardTitle>
              <CardDescription className="mt-2 space-y-1">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Received: {formatDate(result.received_at)}
                  </span>
                  {result.collection_date && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Collected: {formatDate(result.collection_date)}
                    </span>
                  )}
                </div>
              </CardDescription>
            </div>
            <Badge
              className={`text-sm ${
                result.status === 'final'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Critical Alert */}
      {result.has_critical_values && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-800">Critical Values Detected</h4>
              <p className="text-red-700 text-sm mt-1">
                This result contains critical values that require immediate clinical attention.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Results Table */}
      {Object.entries(groupedObservations).map(([category, observations]) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="text-lg">{category}</CardTitle>
            <CardDescription>{observations.length} markers</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                      Marker
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                      Value
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                      Reference Range
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {observations.map((obs, idx) => (
                    <ObservationRow key={idx} observation={obs} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Review Status */}
      {result.reviewed_at ? (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Reviewed</p>
                <p className="text-sm text-green-700">{formatDate(result.reviewed_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Pending Review</p>
                <p className="text-sm text-yellow-700">This result has not been reviewed yet.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Main Page Component
const LabResultsPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<LabResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState<LabResult | null>(null);

  const fetchResults = async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const data = await getPatientResults(patientId);
      setResults(data);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load results';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResults();
  }, [patientId]);

  const handleDownloadPDF = async (resultId: string) => {
    try {
      const blob = await getResultPDF(resultId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lab-results-${resultId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: 'PDF downloaded',
        description: 'Lab results PDF was generated successfully.',
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Download failed',
        description: error?.message || 'Failed to download result PDF.',
        variant: 'destructive',
      });
    }
  };

  // Show detail view if a result is selected
  if (selectedResult) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-6">
        <ResultDetailView
          result={selectedResult}
          onBack={() => setSelectedResult(null)}
          onDownloadPDF={handleDownloadPDF}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FlaskConical className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="font-montserrat text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">Lab Results</h1>
                <p className="mt-1 text-sm text-slate-500">View patient lab test results</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <FlaskConical className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No results available</h3>
                <p className="text-gray-500">Lab results will appear here once they are ready.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {results.map(result => (
              <ResultCard
                key={result.id}
                result={result}
                onViewDetails={setSelectedResult}
                onDownloadPDF={handleDownloadPDF}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LabResultsPage;













